// AdminLayout — wraps every admin route. Verifies the user has the
// `administrator` role by calling /api/admin/me on mount. Renders a
// sidebar with admin sections. Non-admins are redirected to /.
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Users, BarChart3, FileText, ShoppingBag, Image, LogOut, ShieldCheck, Loader2, CreditCard, Film, Share2, Mail, MessageSquare, Send, Sparkles, MapPin, BookOpen, Database, Inbox, Star, Zap, Mic, Settings, Layers, Globe, Bot } from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { useAuth } from "@/lib/auth";

// 2026-10 sidebar restructure — 3 sections requested by the operator:
//   A/ DASHBOARD : App / Iframe / Maps (high-level supervision)
//   B/ THÈME DU MOIS : the AI content orchestration & distribution
//   C/ SETTINGS : operational catalogues + webhooks

const DASHBOARD = [
  { to: "/admin",                end: true, icon: BarChart3, label: "Vue d'ensemble" },
  { to: "/admin/analytics",                 icon: Zap,       label: "Analytics cycles" },
  { to: "/admin/dashboard/app",             icon: Sparkles,  label: "App geoVINUM" },
  { to: "/admin/dashboard/iframe",          icon: Layers,    label: "Iframe" },
  { to: "/admin/dashboard/maps",            icon: Image,     label: "Maps" },
];

// Content orchestration — "Thème du mois" is the central pipeline, the
// other items are the per-channel surfaces that consume it.
const CONTENT = [
  { to: "/admin/monthly-cycle", icon: Bot,            label: "🤖 Cycle du mois (IA)" },
  { to: "/admin/monthly-theme", icon: Sparkles,       label: "Thème du mois", badgeKey: "monthly_theme.draft" },
  { to: "/admin/atlas-ia",      icon: MapPin,         label: "Atlas IA",       badgeKey: "atlas_fiches_to_validate" },
  { to: "/admin/blog",          icon: FileText,       label: "Blog",           badgeKey: "blog_drafts" },
  { to: "/admin/podcast",       icon: Mic,            label: "Podcast" },
  { to: "/admin/newsletter",    icon: Send,           label: "Newsletter",     badgeKey: "newsletter_scheduled" },
  { to: "/admin/app-academy",   icon: BookOpen,       label: "Academy · IA" },
  { to: "/admin/social",        icon: Share2,         label: "Social auto-publish" },
  { to: "/admin/email",         icon: Mail,           label: "Séquences emails" },
];

// Settings & operational tooling
const SETTINGS = [
  { to: "/admin/settings-env",       icon: Settings,    label: "🔑 Variables & clés API" },
  { to: "/admin/gelato/products",    icon: ShoppingBag, label: "Catalog Gelato" },
  { to: "/admin/google-merchant",    icon: Globe,       label: "Google Merchant feeds" },
  { to: "/admin/prints",             icon: Image,       label: "Prints / Gelato orders" },
  { to: "/admin/shop/reviews",       icon: Star,        label: "Avis boutique" },
  { to: "/admin/stripe/webhook",     icon: CreditCard,  label: "Stripe Webhook" },
  { to: "/admin/crm",                icon: Users,       label: "CRM" },
  { to: "/admin/contact-messages",   icon: MessageSquare, label: "Messages contact" },
  { to: "/admin/app-users",          icon: Users,       label: "Users app.geovinum.io" },
  { to: "/admin/app-subscriptions",  icon: CreditCard,  label: "Subscriptions app" },
  { to: "/admin/app-shop-orders",    icon: ShoppingBag, label: "Shop orders app" },
  { to: "/admin/app-rag",            icon: Database,    label: "RAG corpus IA" },
];

// Read a dotted path from an object safely.
const readPath = (obj, path) => {
  if (!obj || !path) return 0;
  if (path === "suggestions_total") {
    const s = obj.suggestions_by_category || {};
    return (s.producer || 0) + (s.appellation || 0) + (s.cepage || 0) + (s.article || 0);
  }
  return path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : 0), obj);
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const { user, ready, logout } = useAuth();
  const [state, setState] = useState({ checking: true, isAdmin: false, error: null });
  const [summary, setSummary] = useState({});

  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate("/login?next=/admin", { replace: true }); return; }
    adminApi.me()
      .then((me) => setState({ checking: false, isAdmin: !!me.is_admin, error: null }))
      .catch((e) => setState({ checking: false, isAdmin: false, error: e.message || String(e) }));
  }, [ready, user, navigate]);

  useEffect(() => {
    if (!state.isAdmin) return undefined;
    // Poll every 30s so badges refresh while the admin browses.
    const fetchSummary = () => {
      const raw = localStorage.getItem("geovinum.auth.v1");
      const t = raw ? JSON.parse(raw)?.token : null;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/inbound/summary`, {
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      }).then(r => r.ok ? r.json() : {}).then(setSummary).catch(() => {});
    };
    fetchSummary();
    const iv = setInterval(fetchSummary, 30000);
    return () => clearInterval(iv);
  }, [state.isAdmin]);

  if (!ready || state.checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--gv-mist)]" data-testid="admin-loading">
        <Loader2 className="animate-spin text-[color:var(--gv-pink)]" size={28} />
      </div>
    );
  }

  if (!state.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--gv-mist)] px-6" data-testid="admin-forbidden">
        <div className="max-w-md bg-white border border-[color:var(--gv-line)] rounded-[5px] p-8 text-center">
          <ShieldCheck size={28} className="mx-auto text-[color:var(--gv-pink)] mb-4" />
          <h2 className="font-display text-2xl text-[color:var(--gv-ink)] mb-2">Admin access required</h2>
          <p className="text-sm text-[color:var(--gv-ink)]/70 mb-5">
            {state.error || "You need an administrator role on academy.geovinum.io to use this panel."}
          </p>
          <Link to="/" className="btn-secondary inline-flex">← Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[color:var(--gv-mist)]" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-[color:var(--gv-ink)] text-white py-6 px-4 flex flex-col">
        <Link to="/" className="block px-2 mb-7">
          <div className="font-display text-xl">geoVINUM</div>
          <div className="text-[10px] uppercase tracking-widest text-white/50">Admin</div>
        </Link>
        <nav className="flex-1 space-y-1 overflow-y-auto">
          {/* A/ DASHBOARD — supervision par surface produit */}
          <div className="px-3 pt-1 pb-2 text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] font-semibold flex items-center gap-1.5">
            <BarChart3 size={11} /> Dashboard
          </div>
          {DASHBOARD.map(({ to, end, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center justify-between gap-2 px-3 py-2 rounded-[4px] text-[13.5px] transition-colors ${
                  isActive ? "bg-[color:var(--gv-pink)] text-white" : "text-white/85 hover:bg-white/10 hover:text-white"
                }`
              }
              data-testid={`admin-nav-dash-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <span className="flex items-center gap-2 truncate"><Icon size={14} /> {label}</span>
            </NavLink>
          ))}

          {/* B/ THÈME DU MOIS — content orchestration */}
          <div className="pt-5 pb-2 px-3 text-[10px] uppercase tracking-widest text-[color:var(--gv-pink)] font-semibold flex items-center gap-1.5">
            <Zap size={11} /> Thème du mois
          </div>
          {CONTENT.map(({ to, icon: Icon, label, badgeKey }) => {
            const count = readPath(summary, badgeKey);
            return (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `flex items-center justify-between gap-2 px-3 py-2 rounded-[4px] text-[13.5px] transition-colors ${
                    isActive ? "bg-[color:var(--gv-pink)] text-white" : "text-white/85 hover:bg-white/10 hover:text-white"
                  }`
                }
                data-testid={`admin-nav-content-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              >
                <span className="flex items-center gap-2 truncate"><Icon size={14} /> {label}</span>
                {count > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-white/15 rounded-full font-mono">{count}</span>
                )}
              </NavLink>
            );
          })}

          {/* C/ SETTINGS — operational tooling */}
          <div className="pt-5 pb-2 px-3 text-[10px] uppercase tracking-widest text-white/40 font-semibold flex items-center gap-1.5">
            <Settings size={11} /> Settings
          </div>
          {SETTINGS.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center justify-between gap-2 px-3 py-2 rounded-[4px] text-[13px] transition-colors ${
                  isActive ? "bg-[color:var(--gv-pink)] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                }`
              }
              data-testid={`admin-nav-settings-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            >
              <span className="flex items-center gap-2 truncate"><Icon size={14} /> {label}</span>
            </NavLink>
          ))}
        </nav>
        <button
          onClick={() => { logout(); navigate("/"); }}
          className="flex items-center gap-2 text-[13px] text-white/70 hover:text-white px-3 py-2 mt-4"
          data-testid="admin-logout"
        >
          <LogOut size={14} /> Sign out
        </button>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 lg:p-10">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
